import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// import { ReactComponent as ChevronDownIcon } from 'src/assets/CaretDown.svg';

import { Container } from 'src/components/Container';
import { Button } from 'src/components/Button';

import { useDispatch } from 'src/store/useDispatch';
import { setTheme } from 'src/store/slice/theme';
import { clearAdminSession } from 'src/store/slice/adminSession';

interface SettingsModalProps {
  onClose: () => void;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`;

const ModalContainer = styled.div<{ $isOpen: boolean; $isLoggedIn: boolean }>`
  position: fixed;
  top: 5rem;
  right: 2rem;
  width: min(320px, calc(100vw - 2rem));
  max-width: calc(100vw - 2rem);
  padding: 1rem;
  background: ${({ theme }) => theme.background};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.grey};
  z-index: 1001;
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)')};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: all 0.2s ease-in-out;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    top: auto;
    bottom: max(1rem, env(safe-area-inset-bottom));
    left: 1rem;
    right: 1rem;
    width: auto;
    max-width: none;
  }
`;

const SettingsSection = styled.div`
  padding: 1rem 0;
`;

const SettingLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.dark};
  font-weight: 600;
`;

const ThemeButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const LogoutSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0 0 0;
  border-top: 1px solid ${({ theme }) => theme.grey};
`;

const LogoutButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.dark};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.negative};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SettingsModal: React.FC<SettingsModalProps> = ({ onClose }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.isDark ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleThemeChange = (mode: any) => {
    setThemeState(mode);
    const isDark = mode === 'dark';
    dispatch(setTheme());
    localStorage.setItem('theme', JSON.stringify({ isDark, system: false }));
  };

  const handleLogout = () => {
    localStorage.removeItem('access');
    dispatch(clearAdminSession());
    onClose();
    window.location.href = '/';
  };

  return (
    <>
      <ModalOverlay $isOpen onClick={onClose} role="presentation" />
      {/* <ModalContainer ref={modalRef} $isOpen={isOpen} $isLoggedIn={isLoggedIn}> */}
      <ModalContainer ref={modalRef} $isOpen $isLoggedIn={true}>
        <SettingsSection>
          <Container display="flex" justifyContent="space-between" alignItems="center" paddingTop="0.75rem" flexWrap="wrap" gap="0.75rem">
            <SettingLabel>Theme</SettingLabel>
            <ThemeButtons>
              <Button
                text="Light"
                className={theme === 'light' ? 'black small' : 'outline small'}
                onClick={() => handleThemeChange('light')}
                disabled={theme === 'light'}
              />
              <Button
                text="Dark"
                className={theme === 'dark' ? 'black small' : 'outline small'}
                onClick={() => handleThemeChange('dark')}
                disabled={theme === 'dark'}
              />
            </ThemeButtons>
          </Container>
        </SettingsSection>

        <LogoutSection>
          <LogoutButton onClick={handleLogout}>
            Log Out
          </LogoutButton>
        </LogoutSection>
      </ModalContainer>
    </>
  );
};

export default SettingsModal;
